/**
 * Pure jurisdiction regime resolution, consent precedence, URL sanitization, session replay masking
 * configuration, and structural funnel payload builders for PostHog analytics (`MAR-1236` / `MAR-1347` / `MAR-1348`).
 *
 * Security & privacy invariants:
 * - Zero confidential legal text (prompts, streamed answers, document names, client/case/party names,
 *   extracted cells) or raw PII (`email`) ever leaves the browser to PostHog.
 * - Strict GDPR / ePrivacy / LGPD opt-in (`'opt-in'` regime) for EU/EEA, UK, Switzerland, and Brazil:
 *   zero PostHog initialization, network requests, or storage prior to explicit opt-in.
 * - Immediate initialization with transparent opt-out (`'opt-out'` regime) for El Salvador, the rest
 *   of Latin America, and the United States, unless the visitor/user has opted out.
 */
import { PLAN_SCREEN_PLAN_IDS } from '../billing/planScreen'

export type AnalyticsRegime = 'opt-in' | 'opt-out'
export type StoredAnalyticsConsent = 'granted' | 'denied' | null

export const ANALYTICS_CONSENT_STORAGE_KEY = 'marcus_analytics_consent'
export const VISITOR_COUNTRY_COOKIE_KEY = 'marcus_visitor_country'
export const ORG_ANALYTICS_CONSENT_AUDIT_ACTION = 'org.analytics_consent_updated' as const

/**
 * ISO 3166-1 alpha-2 country codes requiring strict prior opt-in under GDPR / ePrivacy / UK PECR /
 * Swiss FADP / Brazilian LGPD.
 */
export const OPT_IN_COUNTRIES: ReadonlySet<string> = new Set([
  // EU-27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA non-EU
  'IS', 'LI', 'NO',
  // UK & Switzerland
  'GB', 'UK', 'CH',
  // Brazil (LGPD)
  'BR',
])

/**
 * Non-`Europe/*` IANA timezones that map to an `'opt-in'` jurisdiction when neither an authenticated
 * organization jurisdiction nor a `cf-ipcountry` header is available.
 */
export const OPT_IN_TIMEZONES: ReadonlySet<string> = new Set([
  'Atlantic/Canary',
  'Atlantic/Faeroe',
  'Atlantic/Faroe',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Atlantic/Reykjavik',
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Noronha',
  'America/Maceio',
  'America/Campo_Grande',
  'America/Araguaina',
  'America/Santarem',
  'America/Eirunepe',
  'America/Rio_Branco',
])

/**
 * Normalize a 2-letter ISO country code, ignoring Cloudflare sentinel values (`XX`, `T1`).
 */
export function normalizeCountryCode(code?: string | null): string | null {
  if (typeof code !== 'string') return null
  const trimmed = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(trimmed)) return null
  if (trimmed === 'XX' || trimmed === 'T1') return null
  return trimmed
}

export function isOptInCountry(countryCode?: string | null): boolean {
  const normalized = normalizeCountryCode(countryCode)
  if (!normalized) return false
  return OPT_IN_COUNTRIES.has(normalized)
}

export function isOptInTimezone(timezone?: string | null): boolean {
  if (typeof timezone !== 'string') return false
  const trimmed = timezone.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('Europe/')) return true
  return OPT_IN_TIMEZONES.has(trimmed)
}

/**
 * Resolve the visitor or user's jurisdiction consent regime (`'opt-in'` vs `'opt-out'`).
 *
 * Marcus targets exclusively El Salvador (`SV`) and Latin America (`es_LA`, USD + Salvadoran DTE invoicing),
 * not the EU (Art. 3(2) GDPR targeting criterion does not apply). Analytics therefore runs under a global
 * `'opt-out'` regime across all visitors and users (`MAR-1383`), initializing immediately unless the
 * visitor or user has explicitly opted out via `storedConsent === 'denied'` or `userConsent === false`.
 */
export function resolveAnalyticsRegime(_input?: {
  orgJurisdiction?: string | null
  visitorCountry?: string | null
  timezone?: string | null
}): AnalyticsRegime {
  return 'opt-out'
}

/**
 * Decide whether the floating analytics consent banner should be displayed (`MAR-1383`).
 * The banner never auto-displays on initial page load; it displays only when explicitly opened via
 * `"Preferencias de privacidad"` (`preferencesOpen === true`).
 */
export function shouldShowConsentBanner(input: {
  preferencesOpen: boolean
  bannerDismissed?: boolean
  hasExplicitChoice?: boolean
}): boolean {
  return Boolean(input.preferencesOpen)
}

/**
 * Resolve whether PostHog analytics and masked session replay are effectively permitted right now.
 *
 * Precedence:
 * 1. Authenticated `userConsent` (`userProfile.analytics_consent`) when explicitly `true` or `false`
 * 2. Browser `storedConsent` (`'granted'` | `'denied'`) when explicitly set
 * 3. Regime default (`false` for `'opt-in'`, `true` for `'opt-out'`)
 */
export function resolveEffectiveAnalyticsConsent(input: {
  regime: AnalyticsRegime
  userConsent?: boolean | null
  storedConsent?: StoredAnalyticsConsent
}): boolean {
  if (typeof input.userConsent === 'boolean') {
    return input.userConsent
  }
  if (input.storedConsent === 'granted') return true
  if (input.storedConsent === 'denied') return false
  return input.regime === 'opt-out'
}

/**
 * Decide whether the PostHog client may initialize and send events.
 * Never initializes when `posthogKey` is blank or when effective consent is `false`.
 */
export function shouldInitializePostHog(input: {
  posthogKey?: string | null
  regime: AnalyticsRegime
  userConsent?: boolean | null
  storedConsent?: StoredAnalyticsConsent
}): boolean {
  if (typeof input.posthogKey !== 'string' || !input.posthogKey.trim()) {
    return false
  }
  return resolveEffectiveAnalyticsConsent(input)
}

/**
 * Strip the sensitive `prompt` query parameter from a full URL or relative path so `/app?prompt=...`
 * and `/chat?prompt=...` never leak legal questions into PostHog `$pageview` or `$current_url`.
 */
export function sanitizeAnalyticsUrl(rawUrl: string): string {
  if (!rawUrl || !rawUrl.includes('prompt=')) return rawUrl
  try {
    const isRelative = rawUrl.startsWith('/')
    const parsed = new URL(rawUrl, 'https://holamarcus.local')
    parsed.searchParams.delete('prompt')
    if (isRelative) {
      const search = parsed.searchParams.toString()
      return `${parsed.pathname}${search ? `?${search}` : ''}${parsed.hash}`
    }
    return parsed.toString()
  } catch {
    return rawUrl.replace(/([?&])prompt=[^&#]*(&?)/g, (_m, prefix: string, suffix: string) => {
      if (prefix === '?' && suffix === '&') return '?'
      if (prefix === '&' && suffix === '&') return '&'
      return ''
    })
  }
}

/**
 * Mandatory PostHog session recording privacy configuration (`MAR-1347`).
 */
export const POSTHOG_PERSISTENCE_MODE = 'localStorage+cookie' as const

export const POSTHOG_SESSION_RECORDING_CONFIG = {
  maskAllInputs: true,
  maskTextSelector: '[data-ph-mask]',
  blockSelector: '[data-ph-block], .ph-no-capture',
} as const

// ── Structural Funnel Event Payload Builders (`MAR-1347`) ──────────────────

export function buildLandingQuestionSubmittedPayload(input: {
  country: string
  prompt: string
  wasAuthenticated: boolean
}): {
  country: string
  prompt_length: number
  was_authenticated: boolean
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
    prompt_length: input.prompt.trim().length,
    was_authenticated: Boolean(input.wasAuthenticated),
  }
}

export function buildLandingInlineAuthCompletedPayload(input: {
  method: 'google' | 'password'
  country: string
}): {
  method: 'google' | 'password'
  country: string
} {
  return {
    method: input.method,
    country: normalizeCountryCode(input.country) ?? 'SV',
  }
}

export function buildLandingRoutedAfterAuthPayload(input: {
  destination: '/app' | '/chat'
  hasOrg: boolean
  country: string
}): {
  destination: '/app' | '/chat'
  has_org: boolean
  country: string
} {
  return {
    destination: input.destination,
    has_org: Boolean(input.hasOrg),
    country: normalizeCountryCode(input.country) ?? 'SV',
  }
}

export function buildFreeChatQuestionStartedPayload(input: {
  country: string
  prompt: string
}): {
  country: string
  prompt_length: number
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
    prompt_length: input.prompt.trim().length,
  }
}

export function buildFreeChatQuestionCompletedPayload(input: {
  country: string
  durationMs: number
  citationsCount: number
  freeQueriesUsed: number
  freeQueriesLimit: number
}): {
  country: string
  duration_ms: number
  citations_count: number
  free_queries_used: number
  free_queries_limit: number
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
    duration_ms: Math.max(0, Math.round(input.durationMs)),
    citations_count: Math.max(0, Math.round(input.citationsCount)),
    free_queries_used: Math.max(0, Math.round(input.freeQueriesUsed)),
    free_queries_limit: Math.max(0, Math.round(input.freeQueriesLimit)),
  }
}

export function buildFreeChatLimitReachedPayload(input: {
  country: string
  freeQueriesUsed: number
  freeQueriesLimit: number
}): {
  country: string
  free_queries_used: number
  free_queries_limit: number
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
    free_queries_used: Math.max(0, Math.round(input.freeQueriesUsed)),
    free_queries_limit: Math.max(0, Math.round(input.freeQueriesLimit)),
  }
}

export function buildFreeChatErrorPayload(input: {
  country: string
  statusCode: number
}): {
  country: string
  status_code: number
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
    status_code: Number.isFinite(input.statusCode) ? Math.round(input.statusCode) : 500,
  }
}

export function buildAuthCompletedPayload(input: {
  method: 'google' | 'password'
  mode: 'signin' | 'signup' | 'mfa'
  source: '/login' | 'landing_inline'
}): {
  method: 'google' | 'password'
  mode: 'signin' | 'signup' | 'mfa'
  source: '/login' | 'landing_inline'
} {
  return {
    method: input.method,
    mode: input.mode,
    source: input.source,
  }
}

export function buildDemoCtaClickedPayload(input: {
  surface: 'navbar' | 'landing_cta' | 'login' | 'waitlist' | 'chat_header'
}): {
  surface: 'navbar' | 'landing_cta' | 'login' | 'waitlist' | 'chat_header'
} {
  return {
    surface: input.surface,
  }
}

export function buildFreeChatWaitlistJoinedPayload(input: {
  locale: 'es' | 'en'
  hadQuestion: boolean
}): {
  locale: 'es' | 'en'
  had_question: boolean
} {
  return {
    locale: input.locale,
    had_question: Boolean(input.hadQuestion),
  }
}

export function buildFreeChatCapReachedPayload(input: {
  country?: string | null
}): {
  country: string
} {
  return {
    country: normalizeCountryCode(input.country) ?? 'SV',
  }
}

/** One tier of a purchased basket (MAR-1404), as the analytics payload names it. */
export interface AnalyticsOrderLine { plan_id: string, quantity: number }

/** The basket's lines in snake_case, whole positive quantities only. */
function analyticsLines(lines: ReadonlyArray<{ planId: string, quantity: number }>): AnalyticsOrderLine[] {
  return lines
    .map(line => ({ plan_id: line.planId || 'unknown', quantity: Math.round(line.quantity) }))
    .filter(line => line.quantity > 0)
}

/**
 * `plan_id`, KEPT for the PostHog dashboards built when a checkout bought one plan (MAR-1404). It now means the
 * HIGHEST tier in the basket (Ultra > Pro > Starter > Lite > Redacción; a plan outside those ranks below them), not "the"
 * plan: the whole basket is `lines`. `unknown` for an empty basket.
 */
function highestPlanId(lines: AnalyticsOrderLine[]): string {
  const rank = (planId: string) => (PLAN_SCREEN_PLAN_IDS as readonly string[]).indexOf(planId)
  return lines.reduce<string | null>((top, line) => (top === null || rank(line.plan_id) > rank(top) ? line.plan_id : top), null) ?? 'unknown'
}

/** How many licences: the lines' sum, or `seats` when the lines are not known (a checkout read back); at least 1. */
function analyticsSeats(lines: AnalyticsOrderLine[], seats?: number): number {
  const sum = lines.reduce((total, line) => total + line.quantity, 0)
  return Math.max(1, Math.round(sum || seats || 0))
}

/** `checkout_started`: a basket of licences across tiers (MAR-1404), so `lines` and their total `seats`. */
export function buildCheckoutStartedPayload(input: {
  lines: ReadonlyArray<{ planId: string, quantity: number }>
  totalCents?: number | null
  outcomeKind: string
  hasVoucher: boolean
  source: 'onboarding' | 'billing'
}): {
  /** The highest tier in the basket (see `highestPlanId`), kept for the existing dashboards. */
  plan_id: string
  lines: AnalyticsOrderLine[]
  seats: number
  total_cents: number | null
  outcome_kind: string
  has_voucher: boolean
  source: 'onboarding' | 'billing'
} {
  const lines = analyticsLines(input.lines)
  return {
    plan_id: highestPlanId(lines),
    lines,
    seats: analyticsSeats(lines),
    total_cents: typeof input.totalCents === 'number' ? Math.round(input.totalCents) : null,
    outcome_kind: input.outcomeKind,
    has_voucher: Boolean(input.hasVoucher),
    source: input.source,
  }
}

/** `subscription_purchased`: the basket's `lines` and their total `seats` (MAR-1404). */
export function buildSubscriptionPurchasedPayload(input: {
  lines: ReadonlyArray<{ planId: string, quantity: number }>
  /** The licences billing reports when the lines are not known (a checkout read back after a provider's page). */
  seats?: number
  totalCents?: number | null
  source: 'onboarding' | 'billing'
}): {
  /** The highest tier in the basket (see `highestPlanId`), kept for the existing dashboards. */
  plan_id: string
  lines: AnalyticsOrderLine[]
  seats: number
  total_cents: number | null
  source: 'onboarding' | 'billing'
} {
  const lines = analyticsLines(input.lines)
  return {
    plan_id: highestPlanId(lines),
    lines,
    seats: analyticsSeats(lines, input.seats),
    total_cents: typeof input.totalCents === 'number' ? Math.round(input.totalCents) : null,
    source: input.source,
  }
}
